/**
 * Friend invites (docs/design/05 §8, 07 §3; owner decision Q14: friends = mutual follows): the sender
 * creates a private room, then inserts a `play.invite` row (RLS checks the friendship and blocks); the
 * recipient sees it live through Supabase Realtime (`postgres_changes` on `play.invite`) on any page and
 * accepts (→ the room) or declines.
 */
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { getSupabase, playDb, requireSupabase } from '../../lib/supabase';

/** A `play.invite` row as the client reads it. */
export const InviteRowSchema = z.object({
  id: z.string(),
  from_user: z.string(),
  to_user: z.string(),
  tier: z.number(),
  status: z.enum(['open', 'accepted', 'declined', 'cancelled', 'expired']),
  room_code: z.string().nullable(),
  created_at: z.string(),
  expires_at: z.string(),
});
export type InviteRow = z.infer<typeof InviteRowSchema>;

/** A friend for the invite picker. */
export interface Friend {
  id: string;
  handle: string | null;
  displayName: string | null;
}

/** Mutual follows of the current user, with public profile names. */
export function useFriends(userId: string | null) {
  return useQuery({
    queryKey: ['play', 'friends', userId],
    enabled: userId !== null,
    staleTime: 60_000,
    queryFn: async (): Promise<Friend[]> => {
      const sb = requireSupabase();
      const [following, followers] = await Promise.all([
        sb
          .from('follow')
          .select('followee_id')
          .eq('follower_id', userId ?? ''),
        sb
          .from('follow')
          .select('follower_id')
          .eq('followee_id', userId ?? ''),
      ]);
      if (following.error) throw new Error(following.error.message);
      if (followers.error) throw new Error(followers.error.message);
      const back = new Set((followers.data as { follower_id: string }[]).map((r) => r.follower_id));
      const ids = (following.data as { followee_id: string }[]).map((r) => r.followee_id).filter((id) => back.has(id));
      if (ids.length === 0) return [];
      const profiles = await sb.from('profile').select('user_id, handle, display_name').in('user_id', ids);
      const rows = (profiles.data ?? []) as { user_id: string; handle: string | null; display_name: string | null }[];
      return ids.map((id) => {
        const p = rows.find((r) => r.user_id === id);
        return { id, handle: p?.handle ?? null, displayName: p?.display_name ?? null };
      });
    },
  });
}

/** Sends an invite to a room (the room must exist). */
export async function sendInvite(to: string, roomCode: string, tier: 1 | 2 | 3): Promise<void> {
  const { error } = await playDb().from('invite').insert({ to_user: to, room_code: roomCode, tier });
  if (error)
    throw new Error(
      error.message.includes('row-level security') ? 'You can only invite friends (mutual follows).' : error.message,
    );
}

/** Accepts or declines an invite. */
export async function answerInvite(id: string, accept: boolean): Promise<void> {
  const { error } = await playDb()
    .from('invite')
    .update({ status: accept ? 'accepted' : 'declined' })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

/** Open invites addressed to the user, kept live with Realtime. */
export function useIncomingInvites(userId: string | null): InviteRow[] {
  const [invites, setInvites] = useState<InviteRow[]>([]);
  useEffect(() => {
    const sb = getSupabase();
    if (!sb || !userId) {
      setInvites([]);
      return;
    }
    let active = true;
    const upsert = (row: unknown) => {
      const parsed = InviteRowSchema.safeParse(row);
      if (!parsed.success) return;
      setInvites((list) => {
        const rest = list.filter((i) => i.id !== parsed.data.id);
        return parsed.data.status === 'open' && Date.parse(parsed.data.expires_at) > Date.now()
          ? [parsed.data, ...rest]
          : rest;
      });
    };
    void sb
      .schema('play')
      .from('invite')
      .select('*')
      .eq('to_user', userId)
      .eq('status', 'open')
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        if (active) for (const row of data ?? []) upsert(row);
      });
    const channel = sb
      .channel(`play-invites-${userId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'play', table: 'invite', filter: `to_user=eq.${userId}` },
        (payload) => {
          upsert(payload.new);
        },
      )
      .subscribe();
    return () => {
      active = false;
      void sb.removeChannel(channel);
    };
  }, [userId]);
  return invites;
}

/** Public display names for user ids (invite senders). */
export function useProfileNames(ids: readonly string[]) {
  const key = [...ids].sort().join(',');
  return useQuery({
    queryKey: ['profile-names', key],
    enabled: ids.length > 0,
    staleTime: 300_000,
    queryFn: async (): Promise<Record<string, string>> => {
      const { data } = await requireSupabase()
        .from('profile')
        .select('user_id, handle, display_name')
        .in('user_id', [...ids]);
      const out: Record<string, string> = {};
      for (const r of (data ?? []) as { user_id: string; handle: string | null; display_name: string | null }[]) {
        out[r.user_id] = r.display_name ?? (r.handle ? `@${r.handle}` : 'A friend');
      }
      return out;
    },
  });
}
